import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  FiGlobe,
  FiDownload,
  FiLoader,
  FiAlertCircle,
  FiRefreshCw,
  FiClock,
  FiTrash2,
  FiChevronDown,
  FiChevronUp,
  FiLink,
  FiCheckCircle,
  FiUploadCloud,
  FiVideo,
  FiX,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import api from '../utils/api';
import { canPoll } from '../utils/requestGovernor';

// ─── API helpers ──────────────────────────────────────────────────────────────

const startDubbingJob = async ({ youtubeUrl, videoFile, outputMode }, onUploadProgress) => {
  const formData = new FormData();
  if (videoFile) {
    formData.append('video', videoFile);
  } else {
    formData.append('youtubeUrl', youtubeUrl);
  }
  formData.append('outputMode', outputMode || 'clip');
  const response = await api.post('/dubbing/start', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000,
    onUploadProgress,
  });
  return response.data.data;
};

const getJobStatus  = async (jobId) => { const r = await api.get(`/dubbing/status/${jobId}`); return r.data.data; };
const getVideoUrl   = (jobId) => {
  const token = localStorage.getItem('accessToken');
  const base  = api.defaults.baseURL || '/api';
  return `${base}/dubbing/video/${jobId}?token=${encodeURIComponent(token)}`;
};
const listHistory   = async () => { const r = await api.get('/dubbing/history'); return r.data.data; };
const deleteJob     = async (jobId) => { await api.delete(`/dubbing/${jobId}`); };

// ─── Status config ────────────────────────────────────────────────────────────

const STATUS_LABELS = {
  queued:      'Queued…',
  checking:    'Checking video…',
  downloading: 'Downloading video…',
  dubbing:     'AI Dubbing in progress…',
  merging:     'Merging video & audio…',
  completed:   'Done',
  failed:      'Failed',
};

const STEP_ORDER = ['checking','downloading','dubbing','merging','completed'];

const STEP_PROGRESS = {
  queued: 3, checking: 8, downloading: 20, dubbing: 30, merging: 85, completed: 100,
};

// ─── Video Player ─────────────────────────────────────────────────────────────

const VideoPlayer = ({ videoUrl, title }) => {
  const handleDownload = () => {
    const a = document.createElement('a');
    a.href = videoUrl;
    a.download = `${(title || 'dubbed-video').replace(/[^a-z0-9]/gi, '_')}_telugu.mp4`;
    a.click();
  };

  return (
    <div className="space-y-3">
      <video
        src={videoUrl}
        controls
        className="w-full rounded-xl border border-gray-200 bg-black"
        style={{ maxHeight: 420 }}
      />
      <button
        onClick={handleDownload}
        className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors"
      >
        <FiDownload size={15} />
        Download Telugu Video
      </button>
    </div>
  );
};

// ─── Progress Tracker ─────────────────────────────────────────────────────────

const ProgressTracker = ({ status, progress }) => {
  const pct       = progress || STEP_PROGRESS[status] || 0;
  const activeIdx = STEP_ORDER.indexOf(status);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium text-gray-700">{STATUS_LABELS[status] || status}</span>
        <span className="text-gray-500">{pct}%</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div
          className="bg-primary-500 h-2 rounded-full transition-all duration-700"
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="grid grid-cols-4 gap-1">
        {STEP_ORDER.map((step) => {
          const idx     = STEP_ORDER.indexOf(step);
          const done    = activeIdx > idx;
          const current = activeIdx === idx && status !== 'completed';
          return (
            <div key={step} className="flex flex-col items-center gap-1">
              <div className={`w-2.5 h-2.5 rounded-full transition-colors ${
                done    ? 'bg-green-500' :
                current ? 'bg-primary-500 animate-pulse' :
                          'bg-gray-300'
              }`} />
              <span className={`text-[9px] leading-tight text-center ${
                done || current ? 'text-gray-700 font-medium' : 'text-gray-400'
              }`}>
                {STATUS_LABELS[step]?.replace('…', '') || step}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── History Item ─────────────────────────────────────────────────────────────

const HistoryItem = ({ job, onDelete }) => {
  const [expanded, setExpanded] = useState(false);
  const isDone   = job.status === 'completed';
  const isFailed = job.status === 'failed';
  const videoUrl = isDone ? getVideoUrl(job._id) : null;

  const fmtDur  = (s) => { if (!s) return ''; const m = Math.floor(s/60); return `${m}:${(s%60).toString().padStart(2,'0')}`; };
  const fmtDate = (d) => new Date(d).toLocaleString();
  const label   = job.originalTitle || job.originalFilename || 'Untitled';

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-start gap-3 p-4 text-left hover:bg-gray-50 transition-colors"
      >
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${
          isDone ? 'bg-green-100' : isFailed ? 'bg-red-100' : 'bg-blue-100'
        }`}>
          {isDone   ? <FiCheckCircle size={15} className="text-green-600" />  :
           isFailed ? <FiAlertCircle size={15} className="text-red-500" />    :
                      <FiLoader size={15} className="text-blue-500 animate-spin" />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-800 truncate">{label}</p>
          <div className="flex items-center gap-3 mt-0.5 flex-wrap">
            {job.duration > 0 && (
              <span className="flex items-center gap-1 text-xs text-gray-500">
                <FiClock size={10} /> {fmtDur(job.duration)}
              </span>
            )}
            {job.originalLanguage && <span className="text-xs text-gray-500">{job.originalLanguage}</span>}
            <span className={`text-xs font-medium ${isDone ? 'text-green-600' : isFailed ? 'text-red-500' : 'text-blue-600'}`}>
              {STATUS_LABELS[job.status] || job.status}
            </span>
            {job.costUSD > 0 && <span className="text-xs text-gray-400">${job.costUSD.toFixed(4)}</span>}
          </div>
          <p className="text-[10px] text-gray-400 mt-0.5">{fmtDate(job.createdAt)}</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(job._id); }}
            className="p-1.5 text-gray-400 hover:text-red-500 transition-colors"
            title="Delete"
          >
            <FiTrash2 size={14} />
          </button>
          {expanded ? <FiChevronUp size={14} className="text-gray-400" /> : <FiChevronDown size={14} className="text-gray-400" />}
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-4 border-t border-gray-100 pt-4 space-y-3">
          {isDone && videoUrl ? (
            <VideoPlayer videoUrl={videoUrl} title={label} />
          ) : isFailed ? (
            <div className="flex items-start gap-2 p-3 bg-red-50 rounded-lg text-sm text-red-700">
              <FiAlertCircle size={14} className="mt-0.5 flex-shrink-0" />
              <span>{job.error || 'An unknown error occurred.'}</span>
            </div>
          ) : (
            <p className="text-sm text-gray-500 italic">Processing in progress…</p>
          )}
          {job.youtubeUrl && (
            <a href={job.youtubeUrl} target="_blank" rel="noreferrer"
              className="flex items-center gap-1 text-xs text-primary-600 hover:underline">
              <FiLink size={11} /> Source video
            </a>
          )}
          {(job.whisperMinutes > 0 || job.ttsCharacters > 0) && (
            <div className="text-xs text-gray-400 space-y-0.5">
              {job.whisperMinutes > 0 && <p>Audio: {job.whisperMinutes.toFixed(1)} min</p>}
              {job.ttsCharacters > 0  && <p>Audio generated: {job.ttsCharacters.toLocaleString()} chars</p>}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

export const LanguageChange = () => {
  const [inputMode, setInputMode]         = useState('url');     // 'url' | 'upload'
  const [youtubeUrl, setYoutubeUrl]       = useState('');
  const [videoFile, setVideoFile]         = useState(null);
  const [outputMode, setOutputMode]       = useState('clip');    // 'clip' | 'full'
  const [uploadProgress, setUploadProgress] = useState(0);
  const [loading, setLoading]             = useState(false);
  const [activeJob, setActiveJob]         = useState(null);
  const [activeVideoUrl, setActiveVideoUrl] = useState(null);
  const [history, setHistory]             = useState([]);
  const [showHistory, setShowHistory]     = useState(false);

  const pollRef    = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => { loadHistory(); }, []);
  useEffect(() => () => clearTimeout(pollRef.current), []);

  const loadHistory = async () => {
    try { setHistory(await listHistory() || []); } catch (_) {}
  };

  // ── Polling ───────────────────────────────────────────────────────────────
  // Adaptive interval (quick at first, backs off after 30s) + a shared
  // client-side request budget every polling loop in the app respects (see
  // requestGovernor.js) — dubbing jobs can run for several minutes, so a flat
  // 3s poll could rack up 100+ requests on a single job.

  const startPolling = useCallback((jobId) => {
    clearTimeout(pollRef.current);
    const startedAt = Date.now();

    const tick = async () => {
      if (!canPoll()) {
        pollRef.current = setTimeout(tick, 3000);
        return;
      }
      try {
        const job = await getJobStatus(jobId);
        setActiveJob(job);
        if (job.status === 'completed') {
          setActiveVideoUrl(getVideoUrl(jobId));
          setLoading(false);
          await loadHistory();
          toast.success('Dubbing complete! Telugu video is ready.');
          return; // stop polling
        } else if (job.status === 'failed') {
          setLoading(false);
          await loadHistory();
          toast.error(job.error || 'Dubbing failed. Please try again.');
          return; // stop polling
        }
      } catch (_) {
        setLoading(false);
        return; // stop polling
      }
      const elapsed = Date.now() - startedAt;
      pollRef.current = setTimeout(tick, elapsed < 30000 ? 3000 : 10000);
    };
    pollRef.current = setTimeout(tick, 3000);
  }, []);

  // ── Submit ────────────────────────────────────────────────────────────────

  const handleStart = async () => {
    if (inputMode === 'url' && !youtubeUrl.trim()) { toast.error('Please enter a YouTube URL.'); return; }
    if (inputMode === 'upload' && !videoFile) { toast.error('Please select a video file.'); return; }

    setLoading(true);
    setUploadProgress(0);
    setActiveJob(null);
    setActiveVideoUrl(null);

    try {
      const { jobId } = await startDubbingJob(
        { youtubeUrl: inputMode === 'url' ? youtubeUrl.trim() : '', videoFile: inputMode === 'upload' ? videoFile : null, outputMode },
        (evt) => { if (evt.total) setUploadProgress(Math.round((evt.loaded / evt.total) * 100)); }
      );
      setActiveJob({ status: 'queued', progress: 0 });
      startPolling(jobId);
    } catch (err) {
      setLoading(false);
      const msg = err?.response?.data?.message || 'Failed to start dubbing. Please try again.';
      const isBotBlock = err?.response?.status === 403 || msg.includes('YouTube is blocking');
      if (isBotBlock && inputMode === 'url') {
        setInputMode('upload');
        setYoutubeUrl('');
        toast.error('YouTube blocked this server. Download the video and upload it here instead.', { autoClose: 8000 });
      } else {
        toast.error(msg);
      }
    }
  };

  const handleReset = () => {
    clearTimeout(pollRef.current);
    setYoutubeUrl('');
    setVideoFile(null);
    setUploadProgress(0);
    setActiveJob(null);
    setActiveVideoUrl(null);
    setLoading(false);
  };

  const handleDelete = async (jobId) => {
    try { await deleteJob(jobId); setHistory(prev => prev.filter(j => j._id !== jobId)); }
    catch (_) { toast.error('Failed to delete job.'); }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 200 * 1024 * 1024) { toast.error('File is larger than 200 MB. Please choose a smaller video.'); return; }
    setVideoFile(file);
  };

  const canStart = !loading && (
    (inputMode === 'url'    && youtubeUrl.trim()) ||
    (inputMode === 'upload' && videoFile)
  );

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <FiGlobe size={22} className="text-primary-500" />
          Language Change
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          Convert any video to Telugu audio while preserving the original speaker's voice. Max 5 minutes.
        </p>
      </div>

      {/* Input Card */}
      <div className="bg-white border border-gray-200 rounded-2xl p-6 space-y-5 shadow-sm">

        {/* Mode toggle */}
        <div className="flex rounded-lg bg-gray-100 p-1 gap-1">
          {[
            { key: 'url',    label: 'YouTube URL',   icon: FiLink },
            { key: 'upload', label: 'Upload Video',  icon: FiUploadCloud },
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => { setInputMode(key); setYoutubeUrl(''); setVideoFile(null); }}
              disabled={loading}
              className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                inputMode === key
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Icon size={14} /> {label}
            </button>
          ))}
        </div>

        {/* URL input */}
        {inputMode === 'url' && (
          <div className="space-y-1.5">
            <label className="block text-sm font-medium text-gray-700">YouTube URL</label>
            <div className="relative">
              <FiLink size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="url"
                value={youtubeUrl}
                onChange={e => setYoutubeUrl(e.target.value)}
                placeholder="https://www.youtube.com/watch?v=..."
                disabled={loading}
                className="w-full pl-8 pr-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400 disabled:bg-gray-50"
              />
            </div>
            <p className="text-[11px] text-gray-400">Public YouTube videos only. Max 5 minutes.</p>
          </div>
        )}

        {/* File upload */}
        {inputMode === 'upload' && (
          <div className="space-y-1.5">
            <label className="block text-sm font-medium text-gray-700">Video File</label>
            <input ref={fileInputRef} type="file" accept=".mp4,.mov,video/mp4,video/quicktime" onChange={handleFileChange} className="hidden" />
            {videoFile ? (
              <div className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <FiVideo size={16} className="text-primary-500 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{videoFile.name}</p>
                  <p className="text-xs text-gray-500">{(videoFile.size / (1024*1024)).toFixed(1)} MB</p>
                </div>
                <button onClick={() => setVideoFile(null)} disabled={loading}
                  className="text-gray-400 hover:text-red-500 transition-colors">
                  <FiX size={15} />
                </button>
              </div>
            ) : (
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={loading}
                className="w-full border-2 border-dashed border-gray-300 rounded-xl py-8 flex flex-col items-center gap-2 text-gray-400 hover:border-primary-400 hover:text-primary-500 transition-colors"
              >
                <FiUploadCloud size={28} />
                <span className="text-sm font-medium">Click to select MP4 or MOV</span>
                <span className="text-xs">Max 200 MB · Max 5 minutes</span>
              </button>
            )}

            {/* Upload progress bar (while uploading) */}
            {loading && uploadProgress > 0 && uploadProgress < 100 && (
              <div className="space-y-1">
                <div className="flex justify-between text-xs text-gray-500">
                  <span>Uploading…</span>
                  <span>{uploadProgress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-1.5">
                  <div className="bg-primary-500 h-1.5 rounded-full transition-all" style={{ width: `${uploadProgress}%` }} />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Output length */}
        <div className="space-y-2">
          <label className="block text-xs font-medium text-gray-700">Output Length</label>
          <div className="flex gap-6">
            {[
              { key: 'clip', label: 'Clip to audio length',      desc: 'Video ends when dubbed audio ends' },
              { key: 'full', label: 'Full video length',  desc: 'Original sound fills the remainder' },
            ].map(({ key, label, desc }) => (
              <label key={key} className={`flex items-start gap-2 cursor-pointer select-none ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}>
                <input
                  type="radio"
                  name="outputMode"
                  value={key}
                  checked={outputMode === key}
                  onChange={() => !loading && setOutputMode(key)}
                  className="mt-0.5 accent-primary-600"
                />
                <div>
                  <p className="text-sm font-medium text-gray-800">{label}</p>
                  <p className="text-xs text-gray-500">{desc}</p>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex gap-3">
          <button
            onClick={handleStart}
            disabled={!canStart}
            className="flex-1 flex items-center justify-center gap-2 px-5 py-2.5 bg-primary-600 text-white text-sm font-semibold rounded-xl hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading
              ? <><FiLoader size={15} className="animate-spin" /> Processing…</>
              : <><FiGlobe size={15} /> Convert to Telugu</>}
          </button>
          {(loading || activeJob) && (
            <button onClick={handleReset}
              className="px-4 py-2.5 text-sm text-gray-600 border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors">
              <FiRefreshCw size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Processing progress */}
      {loading && activeJob && (!uploadProgress || uploadProgress >= 100) && (
        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm space-y-4">
          <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
            <FiLoader size={14} className="animate-spin text-primary-500" />
            Converting video to Telugu…
          </h2>
          <ProgressTracker status={activeJob.status} progress={activeJob.progress} />
          <p className="text-xs text-gray-400">
            This may take a few minutes. The speaker's voice is cloned and removed automatically — no voice is stored permanently.
          </p>
        </div>
      )}

      {/* Result */}
      {activeVideoUrl && activeJob?.status === 'completed' && (
        <div className="bg-white border border-green-200 rounded-2xl p-6 shadow-sm space-y-4">
          <div className="flex items-center gap-2">
            <FiCheckCircle size={16} className="text-green-600" />
            <h2 className="text-sm font-semibold text-green-700">Telugu Dubbed Video Ready</h2>
          </div>
          {(activeJob.originalTitle || activeJob.originalFilename) && (
            <p className="text-xs text-gray-500">{activeJob.originalTitle || activeJob.originalFilename}</p>
          )}
          {activeJob.costUSD > 0 && (
            <p className="text-xs text-gray-400">
              API cost: ${activeJob.costUSD.toFixed(4)}
              {activeJob.whisperMinutes > 0 && ` · Audio: ${activeJob.whisperMinutes.toFixed(1)} min`}
              {activeJob.ttsCharacters > 0  && ` · Audio: ${activeJob.ttsCharacters.toLocaleString()} chars`}
            </p>
          )}
          <VideoPlayer videoUrl={activeVideoUrl} title={activeJob.originalTitle || activeJob.originalFilename} />
        </div>
      )}

      {/* Error */}
      {activeJob?.status === 'failed' && !loading && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-5 flex items-start gap-3">
          <FiAlertCircle size={18} className="text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-red-700">Conversion Failed</p>
            <p className="text-sm text-red-600 mt-1">{activeJob.error || 'An unknown error occurred. Please try again.'}</p>
          </div>
        </div>
      )}

      {/* History */}
      {history.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
          <button
            onClick={() => setShowHistory(v => !v)}
            className="w-full flex items-center justify-between px-5 py-4 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <span className="flex items-center gap-2">
              <FiVideo size={15} className="text-gray-400" />
              History ({history.length})
            </span>
            {showHistory ? <FiChevronUp size={15} /> : <FiChevronDown size={15} />}
          </button>
          {showHistory && (
            <div className="px-4 pb-4 space-y-2 border-t border-gray-100 pt-3">
              {history.map(job => (
                <HistoryItem key={job._id} job={job} onDelete={handleDelete} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LanguageChange;
